import { useState, useEffect } from 'react';
import { Card, Button, Table, message, Modal, Form, DatePicker, InputNumber, Tag, Popconfirm, Space } from 'antd';
import { PlusOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';

interface MagicLink {
  id: string;
  token: string;
  formType: string;
  createdAt: string;
  expiresAt: string | null;
  maxUses: number | null;
  usageCount: number;
}

interface MagicLinksManagerProps {
  eventId: string;
}

export default function MagicLinksManager({ eventId }: MagicLinksManagerProps) {
  const [links, setLinks] = useState<MagicLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchLinks();
  }, [eventId]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/feedback/events/${eventId}/magic-links`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setLinks(data);
    } catch (error) {
      message.error('Failed to fetch magic links');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: any) => {
    try {
      await fetch(`/api/v1/feedback/events/${eventId}/magic-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
          maxUses: values.maxUses,
        }),
      });
      message.success('Magic link created successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchLinks();
    } catch (error) {
      message.error('Failed to create magic link');
    }
  };

  const handleDelete = async (linkId: string) => {
    try {
      await fetch(`/api/v1/feedback/events/magic-links/${linkId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      message.success('Magic link deleted successfully');
      fetchLinks();
    } catch (error) {
      message.error('Failed to delete magic link');
    }
  };

  const copyToClipboard = (token: string) => {
    const url = `${window.location.origin}/feedback-form/${token}`;
    navigator.clipboard.writeText(url);
    message.success('Link copied to clipboard!');
  };

  const columns = [
    {
      title: 'Link',
      key: 'link',
      render: (_: any, record: MagicLink) => (
        <div>
          <code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
            /feedback-form/{record.token}
          </code>
        </div>
      ),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_: any, record: MagicLink) => (
        <div>
          {record.usageCount}
          {record.maxUses ? ` / ${record.maxUses}` : ' / ∞'}
        </div>
      ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (expiresAt: string | null) => {
        if (!expiresAt) return <Tag>Never</Tag>;
        const isExpired = new Date(expiresAt) < new Date();
        return (
          <Tag color={isExpired ? 'red' : 'green'}>
            {new Date(expiresAt).toLocaleDateString()}
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: MagicLink) => (
        <Space>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(record.token)}
          >
            Copy
          </Button>
          <Popconfirm
            title="Delete this magic link?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Magic Links"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Generate New Link
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={links}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title="Create Magic Link"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            label="Expiration Date"
            name="expiresAt"
            help="Leave empty for no expiration"
          >
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>

          <Form.Item
            label="Max Uses"
            name="maxUses"
            help="Leave empty for unlimited uses"
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="Unlimited" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Generate Link
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
